import React, { useState, useMemo, useEffect, type ReactNode } from 'react'

import { EditOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { MultipleValue } from '@shared/table/Rules/component'
import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule } from '@shared/table/TableIndex'
import { Button, Space, Modal, Form, Input, InputNumber, Switch, message, Tag, Select, Tooltip, Checkbox } from 'antd'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

// HCT billing_rule 選項（客代類型，必填）
const HCT_BILLING_RULE_OPTIONS = [
  { value: 'by_weight', labelKey: 'enums.shipping_account.billing_rule.by_weight' },
  { value: 'by_cuft', labelKey: 'enums.shipping_account.billing_rule.by_cuft' },
  { value: 'by_length', labelKey: 'enums.shipping_account.billing_rule.by_length' },
]

// ECPAY logistic_subtype 選項（物流類型，必填）
const ECPAY_LOGISTIC_SUBTYPE_OPTIONS = [
  { value: 'B2C', labelKey: 'enums.shipping_account.logistic_subtype.b2c' },
  { value: 'C2C', labelKey: 'enums.shipping_account.logistic_subtype.c2c' }
]

interface CredentialFieldOption {
  value: string;
  labelKey: string;
}

interface CredentialFieldConfig {
  param: string;
  required: boolean;
  type: 'input' | 'password' | 'textarea' | 'select';
  options?: CredentialFieldOption[];
  row?: string;
  hintKey?: string;  // 欄位下方說明文字的 i18n key（Antd Form.Item extra）
}

// 物流商參數配置
const PROVIDER_CREDENTIALS_CONFIG: Record<string, CredentialFieldConfig[]> = {
  hct: [
    { param: 'customer_code', required: true, type: 'input' }, // 客戶代號
    { param: 'cust_id', required: true, type: 'input' },        // 公司名稱(帳號)
    { param: 'password', required: true, type: 'password' },    // 密碼
    { param: 'billing_rule', required: true, type: 'select', options: HCT_BILLING_RULE_OPTIONS, hintKey: 'models.shipping_account.credentials.hct_billing_rule_hint' },  // 客代類型（計費規則）
  ],
  tcat: [
    { param: 'customer_code', required: true, type: 'input' },  // 契客代號
    { param: 'auth_code', required: true, type: 'password' },   // 契客授權碼
  ],
  ecan: [
    { param: 'customer_code', required: true, type: 'input' },  // 客戶代號
  ],
  kerrytj: [
    { param: 'customer_code', required: true, type: 'input' },  // 客戶編號
    { param: 'cust_id', required: true, type: 'input' },        // 公司名稱(帳號)
    { param: 'password', required: true, type: 'password' },    // 密碼
    { param: 'number_start', required: true, type: 'input', row: 'number_range' },
    { param: 'number_end', required: true, type: 'input', row: 'number_range' },
  ],
  tongying: [
    { param: 'customer_code', required: true, type: 'input' },  // 客戶代號
  ],
  // gowarehouse: [
    // { param: 'merchant_key', required: true, type: 'password' },     // wms-console-backend 商家 merchant key
  // ],
  ecpay: [
    { param: 'merchant_id', required: true, type: 'input' },            // 廠商編號
    { param: 'hash_key', required: true, type: 'password' },            // HashKey
    { param: 'hash_iv', required: true, type: 'password' },             // HashIV
    { param: 'logistic_subtype', required: true, type: 'select', options: ECPAY_LOGISTIC_SUBTYPE_OPTIONS },  // 物流類型(B2C/C2C)
  ],
  presco: [
    { param: 'parent_code', required: true, type: 'input' },            // 母代號(3碼)
    { param: 'sub_vendor_code', required: true, type: 'input' },        // 子廠商代號(3碼)
    { param: 'number_start', required: true, type: 'input', row: 'number_range' },
    { param: 'number_end', required: true, type: 'input', row: 'number_range' },
    { param: 'ftp_address', required: true, type: 'input' },
    { param: 'ftp_username', required: true, type: 'input', row: 'ftp_auth', hintKey: 'models.shipping_account.credentials.presco_ftp_username_hint' },
    { param: 'ftp_password', required: true, type: 'password', row: 'ftp_auth' },
  ],
  reyi: [
    { param: 'parent_code', required: true, type: 'input' },            // 母代碼(3碼)
    { param: 'sub_vendor_code', required: true, type: 'input' },        // 子廠商代號(4碼)
    { param: 'api_key', required: true, type: 'password' },             // 廠商公鑰(ApiKey)
    { param: 'secret_key', required: true, type: 'password' },          // 廠商私鑰(SecretKey)
    { param: 'shipper_code', required: true, type: 'input' },           // 大物流代碼
    { param: 'cod_code', required: true, type: 'input' },               // 代收代號
    { param: 'return_cycle', required: true, type: 'input' },           // 退貨週期
    { param: 'return_method', required: true, type: 'input' },          // 退貨方式
    { param: 'number_start', required: true, type: 'input', row: 'number_range' },
    { param: 'number_end', required: true, type: 'input', row: 'number_range' },
    { param: 'ftp_username', required: true, type: 'input', row: 'ftp_auth', hintKey: 'models.shipping_account.credentials.reyi_ftp_username_hint' },
    { param: 'ftp_password', required: true, type: 'password', row: 'ftp_auth' },
    { param: 'ftp_zip_password', required: true, type: 'password' },    // FTP 檔案解壓縮密碼
  ],
  sf: [
    { param: 'customer_code', required: true, type: 'input' },     // 客戶編號
    { param: 'app_key', required: true, type: 'password' },        // appKey
    { param: 'app_secret', required: true, type: 'password' },     // appSecret
    { param: 'aes_key', required: true, type: 'password' },        // encodingAesKey
    { param: 'monthly_card_no', required: true, type: 'input' },   // 月結卡號
    { param: 'sign_key', required: false, type: 'password', hintKey: 'models.shipping_account.credentials.sf_sign_key_hint' },  // 貨態回傳簽名密鑰（順豐提供，未填不同步貨態）
  ],
  dhl: [
    { param: 'customer_code', required: true, type: 'input' },  // Account Number
    { param: 'cust_id', required: true, type: 'input' },        // API Username
    { param: 'password', required: true, type: 'password' },    // API Password
  ]
}

interface ColumnFieldDefinition {
  type: 'input' | 'select' | 'switch';
  required: boolean;
  maxLength?: number;
  options?: CredentialFieldOption[];
}

// 固定欄位的詳細配置（共用）
const COLUMN_FIELD_DEFINITIONS: Record<string, ColumnFieldDefinition> = {
  default_product_name: {
    type: 'input',
    required: false,
    maxLength: 100
  },
  accept_credit_card: {
    type: 'switch',
    required: false
  },
}

// 物流商非 credentials 欄位配置（只記錄有哪些欄位）
const PROVIDER_COLUMNS_CONFIG: Record<string, string[]> = {
  tcat: ['default_product_name', 'accept_credit_card'],
  ecan: ['accept_credit_card'],
  ecpay: ['default_product_name'],
}

// Service 直接呼叫 get / delete / disable / update / create 的方法，
// 因此擴充 TableService 並標出實際被呼叫的 method 簽名。
interface ShippingAccountService extends TableService {
  get: (id: string) => Promise<{ success: boolean; data?: any; message?: string }>;
  delete: (id: string) => Promise<{ success: boolean; message?: string }>;
  disable: (id: string) => Promise<{ success: boolean; message?: string }>;
  update: (id: string, data: Record<string, unknown>) => Promise<{ success: boolean; message?: string; errors?: Record<string, unknown> }>;
  create: (data: Record<string, unknown>) => Promise<{ success: boolean; message?: string; errors?: Record<string, unknown> }>;
}

interface ShippingTypeService {
  catalog: () => Promise<{ success: boolean; data?: CatalogItem[] }>;
}

interface CatalogItem {
  code: string;
  name: string;
  provider: string;
  activated?: boolean;
  [key: string]: unknown;
}

interface MerchantOption {
  id: string;
  name: string;
}

// Credential form 路徑 prefix — 所有 credential 欄位都掛在 form 的 `credentials.*` 巢狀路徑下，
// 跟 shipping_account top-level 欄位（merchant_ids / display_name / provider 等）共用 keyspace
// 會造成同名欄位互相覆蓋（例如 ECPay 的 credential `merchant_id` vs. 貨主 FK `merchant_ids`）。
// 透過 Antd `name={['credentials', field.param]}` 自動分離兩個 namespace。
const CREDENTIALS_FORM_PATH = 'credentials';

interface ShippingAccountRow {
  id: string;
  merchant_ids?: string[];
  merchant_names?: string[];
  warehouse_name?: string | null;
  display_name?: string | null;
  provider?: string | null;
  enabled?: boolean;
  weight_threshold?: number | null;
  deletable?: boolean;
  credentials?: Record<string, unknown>;
  shipping_type_codes?: string[];
  warehouse_id?: string | null;
  accept_credit_card?: boolean;
  default_product_name?: string | null;
  [key: string]: unknown;
}

interface ShippingAccountsListProps {
  service: ShippingAccountService;
  shippingTypeService?: ShippingTypeService; // For fetching shipping type catalog (operator & admin); activation is owned by the backend reconcile on save
  role?: 'operator' | 'admin';
  merchants?: MerchantOption[];
  // 父層（OperatorDataContext）的 fetch in-flight 旗標 — 用 merchants.length 推斷會把
  // 「載入中」跟「合法 0 筆」誤判成同一狀態（前者該轉圈、後者該顯示空清單）
  merchantsLoading?: boolean;
  onCreateReady?: (createFn: () => () => void) => void; // Called with the create function when component mounts
}

const ShippingAccountsList: React.FC<ShippingAccountsListProps> = ({
  service,
  shippingTypeService, // For fetching shipping type catalog (operator & admin); activation is owned by the backend reconcile on save
  role = 'operator',
  merchants = [],
  merchantsLoading = false,
  onCreateReady, // Called with the create function when component mounts
}) => {
  const { t, i18n } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingRecord, setEditingRecord] = useState<ShippingAccountRow | null>(null)
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)
  const [lastModified, setLastModified] = useState(0)
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null)
  const [catalogData, setCatalogData] = useState<CatalogItem[]>([])
  const [selectedShippingTypes, setSelectedShippingTypes] = useState<string[]>([])

  // Fetch shipping type catalog（operator 與 admin 皆可，只要 wrapper 有傳 shippingTypeService）
  const fetchCatalog = async () => {
    if (!shippingTypeService) return
    const result = await shippingTypeService.catalog()
    if (result.success) {
      setCatalogData(result.data || [])
    }
  }

  // Get shipping types for selected provider from catalog
  const providerShippingTypes = useMemo(() => {
    if (!selectedProvider || !catalogData.length) return []
    return catalogData.filter(item => item.provider === selectedProvider)
  }, [selectedProvider, catalogData])

  // 新增模式切換 provider 時，預設全部勾選該 provider 的物流類型
  useEffect(() => {
    if (editingRecord) return // 編輯時由 handleEdit 設定，不覆蓋
    setSelectedShippingTypes(providerShippingTypes.map(t => t.code))
  }, [providerShippingTypes, editingRecord])

  // Handle OAuth callback flash messages from URL parameters
  useEffect(() => {
    const msgKey = searchParams.get('message_key') || searchParams.get('message') // Fallback to 'message' just in case
    const type = searchParams.get('type')

    if (msgKey) {
      const translatedMsg = t(msgKey) as string
      switch (type) {
        case 'success':
          message.success(translatedMsg)
          break
        case 'error':
          message.error(translatedMsg)
          break
        case 'warning':
          message.warning(translatedMsg)
          break
        default:
          message.info(translatedMsg)
      }
      searchParams.delete('message_key')
      searchParams.delete('message')
      searchParams.delete('type')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams, t])

  // 從配置中取得所有已設定的 provider
  const providerOptions = useMemo(() => {
    const configuredProviders = Object.keys(PROVIDER_CREDENTIALS_CONFIG)

    return configuredProviders.map(provider => ({
      value: provider,
      label: t(`providers.${provider}`, { defaultValue: provider })
    }))
  }, [t])

  const handleCreate = () => {
    setEditingRecord(null)
    setSelectedProvider(null)
    setSelectedShippingTypes([])
    form.resetFields()
    form.setFieldsValue({
      enabled: true,
      accept_credit_card: false,
      merchant_ids: [],
    })
    fetchCatalog()
    setIsModalVisible(true)
  }

  // Expose handleCreate to parent component via callback
  useEffect(() => {
    if (onCreateReady) {
      onCreateReady(() => handleCreate)
    }
  }, [onCreateReady])

  // URL 帶 action=new 時自動開啟新增 modal
  useEffect(() => {
    if (searchParams.get('action') === 'new') {
      handleCreate()
      searchParams.delete('action')
      setSearchParams(searchParams, { replace: true })
    }
  }, [])

  const handleEdit = async (id: string) => {
    try {
      fetchCatalog()
      const result = await service.get(id)

      if (result.success) {
        const record = result.data as ShippingAccountRow
        setEditingRecord(record)
        setSelectedProvider(record.provider ?? null)
        // 從記錄中載入已存的 shipping_type_codes
        if (record.shipping_type_codes && record.shipping_type_codes.length > 0) {
          setSelectedShippingTypes(record.shipping_type_codes)
        }

        // credentials 整段塞進 form 的 'credentials' 巢狀路徑 — Antd Form 會自動分發到
        // 對應的 Form.Item name={['credentials', field.param]}，未配置的 key 自然忽略。
        const formValues: Record<string, unknown> = {
          merchant_ids: record.merchant_ids ?? [],
          warehouse_id: record.warehouse_id,
          provider: record.provider,
          display_name: record.display_name,
          enabled: record.enabled,
          accept_credit_card: record.accept_credit_card,
          default_product_name: record.default_product_name,
          weight_threshold: record.weight_threshold,
          [CREDENTIALS_FORM_PATH]: record.credentials || {},
        }

        form.setFieldsValue(formValues)
        setIsModalVisible(true)
      } else {
        message.error((result.message as string) || (t('common.message.fetch_failed') as string))
      }
    } catch (error) {
      console.error('Load error:', error)
      message.error(t('common.message.fetch_failed') as string)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      const result = await service.delete(id)

      if (result.success) {
        message.success(t('common.message.delete_success') as string)
        setLastModified(Date.now())
      } else {
        message.error(t(`common.error.${result.message}`, { defaultValue: result.message || (t('common.error.operation_failed') as string) }) as string)
      }
    } catch (error) {
      console.error('Delete error:', error)
      message.error(t('common.error.operation_failed') as string)
    }
  }

  const handleDisable = async (id: string) => {
    try {
      const result = await service.disable(id)

      if (result.success) {
        message.success(t('common.message.disable_success') as string)
        setLastModified(Date.now())
      } else {
        message.error((result.message as string) || (t('common.error.operation_failed') as string))
      }
    } catch (error) {
      console.error('Disable error:', error)
      message.error(t('common.error.operation_failed') as string)
    }
  }

  const handleEnable = async (id: string) => {
    try {
      const result = await service.update(id, { enabled: true })

      if (result.success) {
        message.success(t('common.message.enable_success', { defaultValue: t('common.message.update_success') }) as string)
        setLastModified(Date.now())
      } else {
        message.error((result.message as string) || (t('common.error.operation_failed') as string))
      }
    } catch (error) {
      console.error('Enable error:', error)
      message.error(t('common.error.operation_failed') as string)
    }
  }

  const handleCancel = () => {
    setIsModalVisible(false)
    setEditingRecord(null)
    setSelectedProvider(null)
    setSelectedShippingTypes([])
    form.resetFields()
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)

      // 巢狀 form path 已經把 credentials 自動聚合在 values.credentials 下，直接拆出即可
      const { [CREDENTIALS_FORM_PATH]: credentials, merchant_ids: vendorMerchantIds, ...rest } = values

      const shippingAccountData: Record<string, unknown> = {
        ...rest,
        // merchant_ids 多選：admin 路徑由 backend 從 token 取 current_merchant 自動帶入，
        // 不送這個 key；operator 路徑送 array（空陣列 = 該倉庫合法但無任何貨主授權）
        ...(role === 'operator' ? { merchant_ids: vendorMerchantIds || [] } : {}),
        // 將勾選的物流類型存入 shipping_type_codes；空陣列也要送出以支援全部取消勾選
        shipping_type_codes: selectedShippingTypes,
      }

      if (credentials && typeof credentials === 'object' && Object.keys(credentials as Record<string, unknown>).length > 0) {
        shippingAccountData.credentials = credentials
      }

      const isCreate = !editingRecord
      let result;

      if (isCreate) {
        result = await service.create(shippingAccountData)
      } else {
        result = await service.update(editingRecord!.id, shippingAccountData)
      }

      if (result.success) {
        // 後端 reconcile_merchant_shipping_types! 已在同一個 save transaction 內原子地
        // activate ShippingType + 建立 MerchantShippingType，FE 不再自行打 activate。
        // 重新拉 catalog 以反映最新的 activated 狀態（fetchCatalog 內部 guard：沒傳 shippingTypeService 即略過）。
        message.success(t(isCreate ? 'common.message.create_success' : 'common.message.update_success') as string)
        fetchCatalog()
        handleCancel()
        setLastModified(Date.now())
      } else if (result.errors) {
        const errorMessages = Object.entries(result.errors)
          .map(([field, messages]) => {
            const msgText = Array.isArray(messages)
              ? messages.map(m => typeof m === 'object' ? JSON.stringify(m) : m).join(', ')
              : (typeof messages === 'object' ? JSON.stringify(messages) : messages)
            return `${field}: ${msgText}`
          })
          .join('\n')
        message.error(errorMessages)
      } else {
        message.error((result.message as string) || (t('common.error.operation_failed') as string))
      }
    } catch (error: any) {
      if (error.errorFields) {
        message.error(t('common.message.please_check_form') as string)
      } else {
        console.error('Save error:', error)
        message.error(t('common.error.operation_failed') as string)
      }
    } finally {
      setSaving(false)
    }
  }

  const columns = useMemo(() => {
    const cols: unknown[] = []

    cols.push(
      {
        nameKey: 'models.shipping_account.display_name',
        tooltip: t('shared.shipping_accounts.list.column_tooltips.display_name'),
        sortable: true,
        sortFieldName: 'shipping_accounts.display_name',
        selector: ({ display_name }: ShippingAccountRow) => display_name || '-',
        grow: 1.5,
      },
      {
        nameKey: 'models.shipping_account.provider',
        tooltip: t('shared.shipping_accounts.list.column_tooltips.provider'),
        sortable: true,
        sortFieldName: 'shipping_accounts.provider',
        cell: ({ provider }: ShippingAccountRow) => {
          if (!provider) return '-'
          return t(`providers.${provider}`, { defaultValue: provider })
        },
        grow: 1.5,
      },
    )

    if (role === 'operator') {
      cols.push({
        nameKey: 'models.shipping_account.warehouse',
        cell: ({ warehouse_name }: ShippingAccountRow) => warehouse_name ? <Tag>{warehouse_name}</Tag> : '-',
        grow: 1.5,
      })
    }

    if (role === 'operator') {
      cols.push({
        nameKey: 'models.shipping_account.merchant',
        tooltip: t('shared.shipping_accounts.list.column_tooltips.merchant'),
        cell: ({ merchant_names }: ShippingAccountRow) => {
          if (!merchant_names || merchant_names.length === 0) {
            // 沒任何 SAM = 該倉庫帳號待補貨主授權 / legacy 共用帳號（resolver 不會 fallback）
            return '-'
          }
          return (
            <Space size={4} wrap>
              {merchant_names.map((name, idx) => (
                <Tag key={idx} color="green">{name}</Tag>
              ))}
            </Space>
          )
        },
        grow: 2,
      })
    }

    cols.push(
      {
        nameKey: 'models.shipping_account.enabled',
        tooltip: t('shared.shipping_accounts.list.column_tooltips.enabled'),
        cell: ({ enabled }: ShippingAccountRow) => (
          <Tag color={enabled ? 'green' : 'default'}>
            {enabled ? t('common.yes') : t('common.no')}
          </Tag>
        ),
        width: '80px',
      },
      {
        nameKey: 'models.shipping_account.weight_threshold',
        tooltip: t('models.shipping_account.weight_threshold_tooltip'),
        cell: ({ weight_threshold }: ShippingAccountRow) =>
          weight_threshold != null
            ? `≥ ${weight_threshold} kg`
            : t('models.shipping_account.no_threshold', { defaultValue: '-' }),
        width: '110px',
      },
      {
        nameKey: 'common.actions',
        width: '180px',
        cell: (record: ShippingAccountRow) => (
          <Space split={<span style={{ color: '#d9d9d9' }}>|</span>} size={4}>
            <Button
              type="link"
              size="small"
              style={{ padding: 0, minWidth: 32 }}
              onClick={() => handleEdit(record.id)}
            >
              {t('common.button.edit')}
            </Button>
            <Button
              type="link"
              size="small"
              style={{ padding: 0, minWidth: 32 }}
              onClick={() => {
                const action = record.enabled ? handleDisable : handleEnable
                const title = record.enabled
                  ? t('common.message.confirm_disable')
                  : t('common.message.confirm_enable', { defaultValue: t('common.message.confirm_update') })
                Modal.confirm({
                  title: title as string,
                  okText: t('common.button.confirm') as string,
                  cancelText: t('common.button.cancel') as string,
                  onOk: () => action(record.id),
                })
              }}
            >
              {record.enabled ? t('common.button.disable') : t('common.button.enable')}
            </Button>
            <Button
              type="link"
              size="small"
              danger
              disabled={record.deletable === false}
              style={{ padding: 0, minWidth: 32 }}
              onClick={() => {
                Modal.confirm({
                  title: t('common.message.confirm_delete') as string,
                  okText: t('common.button.confirm') as string,
                  cancelText: t('common.button.cancel') as string,
                  onOk: () => handleDelete(record.id),
                })
              }}
            >
              {t('common.button.delete')}
            </Button>
          </Space>
        ),
      }
    )

    return cols
  }, [role, t])

  const supportRules = useMemo(() => {
    const rules: SupportRule[] = []

    rules.push({
      label: t('models.shipping_account.provider'),
      component: MultipleValue,
      inputName: 'shipping_accounts.provider',
      createable: false,
      defaultOptions: providerOptions,
    })

    if (role === 'operator' && merchants.length > 0) {
      rules.push({
        label: t('models.shipping_account.merchant'),
        component: MultipleValue,
        inputName: 'shipping_accounts.merchant_ids',
        createable: false,
        defaultOptions: merchants.map(merchant => ({
          value: merchant.id,
          label: merchant.name,
        })),
        tooltip: {
          title: t('shared.shipping_accounts.list.filter_tooltips.merchant.title'),
          content: t('shared.shipping_accounts.list.filter_tooltips.merchant.content'),
        },
      })
    }

    return rules
  }, [role, merchants, providerOptions, t])

  const renderCredentialField = (field: CredentialFieldConfig, providerKey: string): ReactNode => {
    const rules = field.required
      ? [{ required: true, message: t('common.validation.required') }]
      : []

    const placeholder = t(`models.shipping_account.placeholders.${field.param}`, {
      defaultValue: t('common.placeholder.please_enter')
    })

    const label = t([
      `models.shipping_account.credentials.${providerKey}_${field.param}`,
      `models.shipping_account.credentials.${field.param}`
    ])

    // 有 hintKey 時，label 旁加一個 ? icon，hover 顯示說明 tooltip（取代欄位下方 extra）
    const labelNode: ReactNode = field.hintKey ? (
      <span>
        {label as ReactNode}
        <Tooltip
          title={t(field.hintKey, { origin: window.location.origin }) as ReactNode}
          overlayStyle={{ whiteSpace: 'pre-line', maxWidth: 400 }}
        >
          <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', cursor: 'pointer' }} />
        </Tooltip>
      </span>
    ) : (label as ReactNode)

    let inputComponent: ReactNode
    switch (field.type) {
      case 'password':
        inputComponent = <Input.Password placeholder={placeholder as string} />
        break
      case 'textarea':
        inputComponent = <Input.TextArea placeholder={placeholder as string} rows={2} />
        break
      case 'select':
        inputComponent = (
          <Select
            placeholder={t('common.placeholder.please_select') as string}
            allowClear
            options={(field.options || []).map(opt => ({
              value: opt.value,
              label: t(opt.labelKey)
            }))}
          />
        )
        break
      case 'input':
      default:
        inputComponent = <Input placeholder={placeholder as string} />
        break
    }

    return (
      <Form.Item
        key={field.param}
        name={[CREDENTIALS_FORM_PATH, field.param]}
        label={labelNode}
        rules={rules}
        style={field.row ? { flex: 1 } : undefined}
      >
        {inputComponent}
      </Form.Item>
    )
  }

  const renderCredentials = () => {
    if (!selectedProvider) return null

    let providerKey = selectedProvider.toLowerCase()
    const credentials_config = PROVIDER_CREDENTIALS_CONFIG[providerKey]
    const columns_config = PROVIDER_COLUMNS_CONFIG[providerKey]

    // 將 credentials 欄位分組：有 row 的放同一行，沒有的獨立一行
    type GroupedRow =
      | { type: 'row'; fields: CredentialFieldConfig[]; key: string }
      | { type: 'single'; field: CredentialFieldConfig; key: string }
    const groupedCredentials: GroupedRow[] = []
    if (credentials_config) {
      let i = 0
      while (i < credentials_config.length) {
        const field = credentials_config[i]
        if (field.row) {
          // 收集同一 row 的所有欄位
          const rowFields = [field]
          let j = i + 1
          while (j < credentials_config.length && credentials_config[j].row === field.row) {
            rowFields.push(credentials_config[j])
            j++
          }
          groupedCredentials.push({ type: 'row', fields: rowFields, key: field.row })
          i = j
        } else {
          groupedCredentials.push({ type: 'single', field, key: field.param })
          i++
        }
      }
    }

    return (
      <>
        {groupedCredentials.map(group => {
          if (group.type === 'row') {
            return (
              <div key={group.key} style={{ display: 'flex', gap: '16px' }}>
                {group.fields.map(f => renderCredentialField(f, providerKey))}
              </div>
            )
          }
          return renderCredentialField(group.field, providerKey)
        })}

        {/* ===== ShippingAccount 非 credentials 參數區塊 ===== */}
        {columns_config && columns_config.map((fieldParam) => {
          const fieldDef = COLUMN_FIELD_DEFINITIONS[fieldParam]
          if (!fieldDef) return null

          const rules: Array<Record<string, unknown>> = []
          if (fieldDef.required) {
            rules.push({ required: true, message: t('common.validation.required') })
          }
          if (fieldDef.maxLength) {
            rules.push({ max: fieldDef.maxLength, message: t('common.validation.max_length', { count: fieldDef.maxLength }) })
          }

          const label = t(`models.shipping_account.${fieldParam}`)
          const placeholder = t(`models.shipping_account.placeholders.${fieldParam}`, {
            defaultValue: t('common.placeholder.please_enter')
          })

          let inputComponent: ReactNode
          switch (fieldDef.type) {
            case 'select':
              inputComponent = (
                <Select
                  placeholder={t('common.placeholder.please_select') as string}
                  options={(fieldDef.options || []).map(opt => ({
                    value: opt.value,
                    label: t(opt.labelKey)
                  }))}
                />
              )
              break
            case 'switch':
              inputComponent = <Switch />
              break
            case 'input':
            default:
              inputComponent = (
                <Input placeholder={placeholder as string} maxLength={fieldDef.maxLength} />
              )
              break
          }

          return (
            <Form.Item
              key={fieldParam}
              name={fieldParam}
              label={label as ReactNode}
              rules={rules}
              valuePropName={fieldDef.type === 'switch' ? 'checked' : 'value'}
            >
              {inputComponent}
            </Form.Item>
          )
        })}
      </>
    )
  }

  return (
    <>
      <TableIndex
        columns={columns}
        searchable={true}
        columnsEditable={true}
        tableId={`${role === 'operator' ? 'Operator' : 'Admin'}ShippingAccountsTable`}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        service={service}
        filterHelpExample={t('shared.shipping_accounts.list.filter_help_example') as string}
        filterDocsUrl={`/docs/${role}/shipping_accounts/list#features`}
        searchTooltipContent={t('shared.shipping_accounts.list.search_tooltip') as ReactNode}
      />

      {/* 新增/編輯 Modal */}
      <Modal
        title={
          editingRecord
            ? `${t('common.button.edit')} ${t('models.shipping_account._')}`
            : `${t('common.button.new')} ${t('models.shipping_account._')}`
        }
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        confirmLoading={saving}
        okText={t('common.button.save') as string}
        cancelText={t('common.button.cancel') as string}
        width={1200}
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
      >
        <div style={{ display: 'flex', gap: '16px', marginTop: 24 }}>
          {/* 左側：帳號設定 */}
          <div style={{ flex: 1, borderRight: '1px solid #f0f0f0', paddingRight: '16px' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', color: 'rgba(0, 0, 0, 0.88)' }}>
              {t('models.shipping_account.general_settings_title')}
            </div>
            <Form
              form={form}
              layout="vertical"
            >
              {/* Provider + Display name 第一行（最重要） */}
              <div style={{ display: 'flex', gap: '16px' }}>
                <Form.Item
                  name="provider"
                  label={t('models.shipping_account.provider') as ReactNode}
                  rules={[
                    { required: true, message: t('common.validation.required') },
                  ]}
                  style={{ flex: 1 }}
                >
                  <Select
                    placeholder={t('common.placeholder.please_select') as string}
                    showSearch
                    disabled={!!editingRecord}
                    onChange={(value) => setSelectedProvider(value)}
                    filterOption={(input, option) =>
                      ((option?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={providerOptions}
                  />
                </Form.Item>

                <Form.Item
                  name="display_name"
                  label={t('models.shipping_account.display_name') as ReactNode}
                  rules={[
                    { required: true, message: t('common.validation.required') },
                    { max: 100, message: t('common.validation.max_length', { count: 100 }) },
                  ]}
                  style={{ flex: 1 }}
                >
                  <Input
                    placeholder={t('models.shipping_account.placeholders.display_name') as string}
                    maxLength={100}
                  />
                </Form.Item>
              </div>

              {/* 物流類型勾選 - 選了 provider 之後顯示（operator 與 admin 皆有，catalog 由 shippingTypeService 提供）*/}
              {selectedProvider && providerShippingTypes.length > 0 && (
                <Form.Item
                  label={t('operator.pages.logistics.shipping_accounts.shipping_types_label') as ReactNode}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                    {providerShippingTypes.map(type => (
                      <Checkbox
                        key={type.code}
                        checked={selectedShippingTypes.includes(type.code)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedShippingTypes(prev => [...prev, type.code])
                          } else {
                            setSelectedShippingTypes(prev => prev.filter(c => c !== type.code))
                          }
                        }}
                      >
                        {type.name}
                      </Checkbox>
                    ))}
                  </div>
                </Form.Item>
              )}

              {/* Merchant 多選 - Operator only，倉庫由後端自動帶入；
                  空陣列 = 該倉庫合法但待補貨主授權（resolver 不會 fallback 找到，
                  需後續補上 SAM 才能被出貨流程選中）*/}
              {role === 'operator' && (
                <Form.Item
                  name="merchant_ids"
                  label={t('models.shipping_account.merchants') as ReactNode}
                  extra={t('operator.pages.logistics.shipping_accounts.merchants_help') as ReactNode}
                >
                  <Select
                    mode="multiple"
                    placeholder={t('common.placeholder.please_select') as string}
                    showSearch
                    loading={merchantsLoading}
                    filterOption={(input, option) =>
                      ((option?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={merchants.map(merchant => ({
                      value: merchant.id,
                      label: merchant.name,
                    }))}
                  />
                </Form.Item>
              )}

              <Form.Item
                name="weight_threshold"
                label={
                  <span>
                    {t('models.shipping_account.weight_threshold')}
                    <Tooltip title={t('models.shipping_account.weight_threshold_tooltip') as ReactNode}>
                      <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', cursor: 'pointer' }} />
                    </Tooltip>
                  </span>
                }
              >
                <InputNumber
                  min={0}
                  step={0.1}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder={t('models.shipping_account.placeholders.weight_threshold', { defaultValue: '例：5.00' }) as string}
                  addonBefore="≥"
                  addonAfter="kg"
                />
              </Form.Item>



            </Form>
          </div>

          {/* 右側：物流商串接設定 */}
          <div style={{ flex: 1, paddingLeft: '16px', paddingRight: '8px' }}>
            <Form
              form={form}
              layout="vertical"
            >
              {selectedProvider && (
                <>
                  <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', color: 'rgba(0, 0, 0, 0.88)' }}>
                    {t(`providers.${selectedProvider}`, { defaultValue: selectedProvider })} {t(`models.shipping_account.title`)}
                  </div>
                  {renderCredentials()}
                </>
              )}
            </Form>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default ShippingAccountsList
